/*
  Collection grid: filtering, the filter drawer, its accordions, and the
  density toggle.

  Filters are applied on Apply, not as they are ticked. Refetching on every
  change leaves Apply with nothing to do and makes choosing four filters
  cost four round trips. Applying then updates the page in place: the same
  URL is fetched with section_id appended, which returns only this section's
  markup, and the pieces that changed are swapped in.

  Only the changed parts are replaced rather than the whole section: the
  drawer has to stay open and keep its scroll position while filtering.

  The accordions are a button and a panel rather than <details>, because a
  closed <details> stops rendering its content entirely and so has nothing
  to animate.
*/
if (!customElements.get('collection-grid')) {
  const DENSITY_KEY = 'collection-density';

  class CollectionGrid extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.drawer = this.querySelector('[data-filter-drawer]');
      this.grid = this.querySelector('[data-grid]');
      this.form = this.querySelector('[data-facet-form]');

      this.onKeydown = this.onKeydown.bind(this);
      this.onPopState = this.onPopState.bind(this);

      this.addEventListener('click', this.onClick.bind(this));

      if (this.form) {
        this.form.addEventListener('submit', (event) => {
          event.preventDefault();
          this.apply();
          this.closeFilters();
        });
      }

      window.addEventListener('popstate', this.onPopState);

      this.restoreDensity();
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.onKeydown);
      window.removeEventListener('popstate', this.onPopState);
      document.body.classList.remove('filters-open');
    }

    onClick(event) {
      if (event.target.closest('[data-filter-open]')) {
        this.openFilters();
        return;
      }

      if (event.target.closest('[data-filter-close]')) {
        this.closeFilters();
        return;
      }

      const toggle = event.target.closest('[data-group-toggle]');
      if (toggle) {
        this.toggleGroup(toggle);
        return;
      }

      const density = event.target.closest('[data-density]');
      if (density) {
        this.setDensity(density.dataset.density);
        return;
      }

      // Chips, Clear all and pagination are all plain links carrying a URL
      // Shopify built. Intercepting them keeps the page in place. These do
      // act immediately: each is a single deliberate action, unlike ticking
      // boxes on the way to a combination.
      const link = event.target.closest('[data-chip], [data-clear], .collection__page, .collection__more');
      if (link && link.href) {
        event.preventDefault();
        this.navigate(link.href, link.matches('.collection__page, .collection__more'));
      }
    }

    /* Filtering ----------------------------------------------------------- */

    apply() {
      if (!this.form) return;

      const params = new URLSearchParams();
      new FormData(this.form).forEach((value, key) => {
        // An empty price field means no bound, not a bound of nothing.
        if (value !== '') params.append(key, value);
      });

      const query = params.toString();
      this.navigate(`${window.location.pathname}${query ? `?${query}` : ''}`);
    }

    async navigate(url, scrollToTop = false) {
      const target = new URL(url, window.location.origin);

      // A newer request supersedes one still in flight, which otherwise
      // lands later and overwrites it.
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();

      this.setAttribute('loading', '');

      const separator = target.search ? '&' : '?';
      const fetchUrl = `${target.pathname}${target.search}${separator}section_id=${this.sectionId}`;

      try {
        const response = await fetch(fetchUrl, { signal: this.controller.signal });
        if (!response.ok) throw new Error('Request failed');

        const markup = await response.text();
        const parsed = new DOMParser().parseFromString(markup, 'text/html');

        this.swap(parsed);
        window.history.pushState({}, '', target.toString());

        if (scrollToTop) {
          this.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      } catch (error) {
        if (error.name === 'AbortError') return;

        // Fall back to a normal page load rather than leaving the shopper
        // with controls that appear to do nothing.
        window.location.href = target.toString();
      } finally {
        this.removeAttribute('loading');
      }
    }

    swap(parsed) {
      this.replaceNode(parsed, '[data-grid]');
      this.replaceNode(parsed, '.collection__toolbar');
      this.replaceNode(parsed, '.collection__pagination');
      this.replaceNode(parsed, '.collection__more-wrap');
      this.replaceChips(parsed);
      this.replaceFacets(parsed);

      // The grid element is new, so the reference and its density class are
      // both stale.
      this.grid = this.querySelector('[data-grid]');
      this.restoreDensity();
    }

    replaceNode(parsed, selector) {
      const next = parsed.querySelector(selector);
      const current = this.querySelector(selector);

      if (next && current) current.replaceWith(next);
      else if (current) current.remove();
    }

    replaceChips(parsed) {
      // The applied row appears and disappears, so it cannot simply be
      // swapped: it may be absent on either side.
      const nextRow = parsed.querySelector('.collection__applied');
      const currentRow = this.querySelector('.collection__applied');

      if (nextRow && currentRow) currentRow.replaceWith(nextRow);
      else if (nextRow) this.querySelector('.collection__toolbar').after(nextRow);
      else if (currentRow) currentRow.remove();
    }

    replaceFacets(parsed) {
      const next = parsed.querySelector('.filter-drawer__body');
      const current = this.querySelector('.filter-drawer__body');
      if (!next || !current) return;

      // Counts and availability change with every filter, so the groups have
      // to be re-rendered. Which ones were open, and how far down the list
      // the shopper had scrolled, are theirs and are carried across.
      const openGroups = Array.from(current.querySelectorAll('[data-group]')).map((group) =>
        group.hasAttribute('open')
      );
      const scrollTop = current.scrollTop;

      current.replaceWith(next);

      next.querySelectorAll('[data-group]').forEach((group, index) => {
        if (!openGroups[index]) return;

        group.setAttribute('open', '');
        const toggle = group.querySelector('[data-group-toggle]');
        if (toggle) toggle.setAttribute('aria-expanded', 'true');
      });

      next.scrollTop = scrollTop;
    }

    onPopState() {
      this.navigate(window.location.href);
    }

    /* Accordions ---------------------------------------------------------- */

    toggleGroup(toggle) {
      const group = toggle.closest('[data-group]');
      if (!group) return;

      const isOpen = group.hasAttribute('open');
      group.toggleAttribute('open', !isOpen);
      toggle.setAttribute('aria-expanded', String(!isOpen));
    }

    /* Drawer -------------------------------------------------------------- */

    openFilters() {
      if (!this.drawer) return;
      this.drawer.setAttribute('open', '');
      document.body.classList.add('filters-open');
      document.addEventListener('keydown', this.onKeydown);

      const close = this.drawer.querySelector('[data-filter-close]');
      if (close) close.focus();
    }

    closeFilters() {
      if (!this.drawer) return;
      this.drawer.removeAttribute('open');
      document.body.classList.remove('filters-open');
      document.removeEventListener('keydown', this.onKeydown);

      const opener = this.querySelector('[data-filter-open]');
      if (opener) opener.focus();
    }

    onKeydown(event) {
      if (event.key === 'Escape') this.closeFilters();
    }

    /* Density ------------------------------------------------------------- */

    setDensity(value) {
      if (!this.grid) return;

      this.grid.classList.toggle('product-grid--dense', value === 'dense');

      this.querySelectorAll('[data-density]').forEach((button) => {
        button.toggleAttribute('aria-current', button.dataset.density === value);
      });

      // Nice to have, not load bearing, so a blocked storage API is ignored.
      try {
        localStorage.setItem(DENSITY_KEY, value);
      } catch (error) {
        // Private browsing or storage disabled.
      }
    }

    restoreDensity() {
      let stored = null;
      try {
        stored = localStorage.getItem(DENSITY_KEY);
      } catch (error) {
        // Ignored, falls back to the default density.
      }

      if (stored) this.setDensity(stored);
    }
  }

  customElements.define('collection-grid', CollectionGrid);
}
